// The two things that get somebody from "I have never done this" to a file we
// can read: an example, and a prompt for the model they already use.
//
// Pure text, in a module with no React in it, so the smoke test can put the
// samples through the real parser. A sample file that does not import is worse
// than no sample at all — it teaches the wrong format and then blames the
// person who followed it.

import type { BrandDetail, DraftKind } from "./types";

const KIND_NOUN: Record<DraftKind, string> = {
  caption: "social media captions",
  description: "short photo descriptions",
  punchline: "one-line punch lines",
  title: "YouTube video titles",
};

/**
 * What to paste into ChatGPT, Claude or Gemini.
 *
 * Written as instructions to a model, so it says the format twice and says it
 * bluntly: the single most common way an import goes wrong is a model that
 * helpfully wraps the answer in "Here are your 10 captions!" and numbers them.
 */
export function promptFor(
  kind: DraftKind, brandName: string, details: BrandDetail[],
): string {
  const tokens = details.length > 0
    ? details.map((d) => `  {{${d.key}}}  — ${d.label.toLowerCase()}`).join("\n")
    : "  (none set yet — add them on the caption library first)";

  return `You are writing ${KIND_NOUN[kind]} for ${brandName}.

FORMAT. This matters more than anything else below.
Return ONLY the ${kind === "caption" ? "captions" : "lines"} themselves. Separate each one with a
line containing exactly three dashes:

---

No numbering. No headings. No "Here are your captions". No commentary before or
after. If you add anything that is not a ${kind}, it will be imported as one.

PLACEHOLDERS. Write these EXACTLY as shown — do not replace them with real
values, and do not guess at what they contain:

${tokens}

They are filled in automatically with the current value when the ${kind} is used,
so a link that changes next month stays correct in everything already written.

ANYTHING ELSE YOU DO NOT KNOW goes in square brackets: [the date], [the price],
[what the class is called]. Never invent a fact — no prices, dates, names,
qualifications, results or claims that were not given to you. A visible
[bracket] gets filled in by a person; an invented fact gets published.

WHAT TO WRITE.
- How many: 10
- What about: [tell it here — a new class, an offer, a week of daily posts]
- Voice: [warm and plain / brisk / funny — pick one]
- Keep them short. The first line or two is all most people read.
- End each one with a single clear ask. Instagram and YouTube have no
  call-to-action button for posts like these, so the ask has to be in the words.
- At most three hashtags, and only ones that are real.
- Vary the openings. Ten ${kind}s that all start the same way read as one.

Then save the answer as a .txt or .md file, or paste it into the first column of
a spreadsheet, and import it.`;
}

export const CSV_SAMPLE = `caption
"Open late on Thursdays from [date] — last appointment 8pm. Book at {{booking}}."
"Three spaces left on the beginners' course starting [date]. {{booking}}"
"""Best decision I made this year."" — [first name], [what they came in for]"
"Meet [name], our [role]. [One human detail.] Say hello when you are next in."
"Sore after a long drive? Two things that help, in order: [first], [second]."
`;

export const MD_SAMPLE = `Open late on Thursdays from [date] — last appointment 8pm.

Book at {{booking}}.

---

Three spaces left on the beginners' course starting [date].

[Who it suits.] [What it costs.]

{{booking}}

---

"Best decision I made this year."
— [first name], [what they came in for]

[One line on what you did.]

Start the same way: {{booking}}
`;
